import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

import { enableTiltHover } from '../utils/tilt-hover.js';

const POINTER_MQ = '(hover: hover) and (pointer: fine)';

export default class BlogListing {
  constructor(container) {
    this.container = container;
    this.sectionId =
      container.dataset.sectionId ||
      container.closest('[data-section-id]')?.dataset.sectionId;
    this.filterAbort = null;
    this.pointerMq = window.matchMedia(POINTER_MQ);

    this.init();
    this.container.addEventListener('infinite-scroll:append', this.onAppend);
    document.addEventListener('blog:filter', this.onFilterEvent);
    window.addEventListener('popstate', this.onPopState);
  }

  destroy = () => {
    document.removeEventListener('blog:filter', this.onFilterEvent);
    window.removeEventListener('popstate', this.onPopState);
  };

  onFilterEvent = (e) => {
    if (e.detail?.url) this.applyFilter(e.detail.url);
  };

  init = () => {
    this.initCards();
    this.initHover();
  };

  initCards = () => {
    this.cards = this.container.querySelectorAll('.blog-listing__card:not([data-anim-ready])');
    if (this.cards.length === 0) return;

    this.cards.forEach((card) => card.setAttribute('data-anim-ready', 'true'));
    gsap.set(this.cards, { opacity: 0, yPercent: 40 });

    ScrollTrigger.batch(this.cards, {
      interval: 0.2,
      batchMax: 2,
      start: 'top-=40px bottom',
      onEnter: (elements) => {
        gsap.to(elements, {
          opacity: 1,
          yPercent: 0,
          duration: 1.6,
          stagger: { each: 0.1 },
          overwrite: 'auto',
          ease: 'expo.out',
        });
      },
    });
  };

  initHover = () => {
    if (!this.pointerMq.matches) return;

    const cards = this.container.querySelectorAll('.blog-listing__card:not([data-hover-ready])');
    cards.forEach((card) => {
      const media = card.querySelector('.blog-listing__media');
      const cursor = card.querySelector('.blog-listing__hover');
      const photo = media?.querySelector('.image') || media?.querySelector('img');
      if (!media || !cursor || !photo) return;

      card.setAttribute('data-hover-ready', 'true');
      enableTiltHover({ area: media, photo, cursor });
    });
  };

  onAppend = () => {
    this.init();
  };

  onPopState = () => {
    if (!document.contains(this.container)) return;
    this.applyFilter(window.location.href, { push: false });
  };

  setActiveTag = (url) => {
    const next = new URL(url, window.location.origin);
    let activeTag = null;
    document.querySelectorAll('[data-blog-tags] .blog-hero__tag').forEach((tag) => {
      const tagUrl = new URL(tag.href, window.location.origin);
      const isAll = !tagUrl.pathname.includes('/tagged/');
      const active = isAll
        ? !next.pathname.includes('/tagged/')
        : next.pathname === tagUrl.pathname;
      tag.classList.toggle('is-active', active);
      if (active) activeTag = tag;
    });
    // The hero row is a transform-driven marquee (blog-hero.js), so it can't be
    // moved with scrollIntoView — let it bring the active tag into view itself.
    if (activeTag) document.dispatchEvent(new CustomEvent('blog:tags-changed'));
  };

  applyFilter = async (rawUrl, { push = true } = {}) => {
    if (!rawUrl || !this.sectionId) return;
    if (this.filterAbort) this.filterAbort.abort();
    this.filterAbort = new AbortController();
    this.container.classList.add('is-filtering');

    const url = new URL(rawUrl, window.location.origin);
    const sectionUrl = new URL(url.toString());
    sectionUrl.searchParams.set('section_id', this.sectionId);

    try {
      const { reinitInfiniteScroll } = await import('../utils/infinite-scroll.js');
      const response = await fetch(sectionUrl.toString(), {
        signal: this.filterAbort.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incoming =
        doc.querySelector('[data-section-script="blog-listing"]') || doc.body.firstElementChild;
      if (!incoming) return;

      this.container.replaceWith(incoming);
      incoming.dataset.sectionLoaded = 'true';
      reinitInfiniteScroll(incoming.querySelector('[data-infinite-scroll]'));
      if (push) window.history.pushState({}, '', url.toString());
      this.setActiveTag(url.toString());
      this.destroy();
      new BlogListing(incoming);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Blog filter failed:', error);
      this.container?.classList.remove('is-filtering');
    } finally {
      this.filterAbort = null;
    }
  };
}
